import React, { useState } from 'react';
import { X, Calendar, Phone, User, Wrench, CheckCircle, Loader2, MessageCircle } from 'lucide-react';
import { getSiteSession } from '../lib/track';

const SERVICES = [
  { id: 'notebook',  label: 'Notebook',  emoji: '💻' },
  { id: 'desktop',   label: 'Desktop',   emoji: '🖥️' },
  { id: 'monitor',   label: 'Monitor',   emoji: '🖱️' },
  { id: 'nobreak',   label: 'No-break',  emoji: '🔋' },
  { id: 'outro',     label: 'Outro',     emoji: '🔧' },
];

const API = process.env.REACT_APP_BACKEND_URL;

const AgendarDiagnosticoModal = ({ isOpen, onClose }) => {
  const [service, setService]   = useState('');
  const [name, setName]         = useState('');
  const [phone, setPhone]       = useState('');
  const [date, setDate]         = useState('');
  const [message, setMessage]   = useState('');
  const [status, setStatus]     = useState('idle'); // idle | loading | success | error

  const reset = () => {
    setService(''); setName(''); setPhone('');
    setDate(''); setMessage(''); setStatus('idle');
  };

  const handleClose = () => { reset(); onClose(); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!service || !name || !phone) return;
    setStatus('loading');

    const body = {
      name,
      email: 'agendamento@site.com', // placeholder requerido pelo backend
      phone,
      message: `SOLICITAÇÃO DE DIAGNÓSTICO\n\nEquipamento: ${SERVICES.find(s => s.id === service)?.label}\nData preferida: ${date || 'A combinar'}\n\n${message || 'Sem observações adicionais'}`,
      session_id: getSiteSession(),
    };

    try {
      const res = await fetch(`${API}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        setStatus('success');
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  const whatsappLink = () => {
    const svc = SERVICES.find(s => s.id === service)?.label || 'equipamento';
    const txt = `Olá! Gostaria de agendar um diagnóstico para meu ${svc}.${name ? ` Meu nome é ${name}.` : ''}${date ? ` Data preferida: ${date}.` : ''}`;
    return `https://wa.me/5516991378312?text=${encodeURIComponent(txt)}`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[400] flex items-center justify-center px-4" data-testid="agendar-modal">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={handleClose} />

      {/* Panel */}
      <div className="relative bg-[#0f172a] border border-slate-700/60 rounded-2xl shadow-2xl shadow-black/60 w-full max-w-lg overflow-hidden"
           style={{ animation: 'modalSlideUp 0.2s cubic-bezier(0.34,1.2,0.64,1) both' }}>
        <style>{`
          @keyframes modalSlideUp {
            from { opacity: 0; transform: translateY(20px) scale(0.97); }
            to   { opacity: 1; transform: translateY(0) scale(1); }
          }
        `}</style>

        {/* Header */}
        <div className="relative bg-gradient-to-r from-violet-700 to-cyan-700 px-6 py-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
              <Wrench className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-white font-bold text-lg leading-tight">Agendar Diagnóstico</h2>
              <p className="text-white/70 text-xs">Gratuito — sem compromisso</p>
            </div>
          </div>
          <button onClick={handleClose} className="absolute top-4 right-4 text-white/70 hover:text-white transition-colors p-1 rounded-lg hover:bg-white/10">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          {status === 'success' ? (
            <div className="text-center py-6">
              <CheckCircle className="w-14 h-14 text-emerald-400 mx-auto mb-4" />
              <h3 className="text-white font-bold text-lg mb-2">Agendamento recebido!</h3>
              <p className="text-slate-400 text-sm mb-6">Entraremos em contato em breve para confirmar o horário.</p>
              <button onClick={handleClose} className="px-6 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-full font-semibold text-sm transition-colors">
                Fechar
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">

              {/* Tipo de equipamento */}
              <div>
                <label className="text-slate-400 text-xs font-semibold uppercase tracking-wider mb-2 block">
                  Equipamento <span className="text-red-400">*</span>
                </label>
                <div className="flex flex-wrap gap-2">
                  {SERVICES.map(s => (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => setService(s.id)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-sm font-medium transition-all ${
                        service === s.id
                          ? 'bg-cyan-600 border-cyan-500 text-white'
                          : 'bg-slate-800/60 border-slate-700 text-slate-400 hover:border-cyan-500/50 hover:text-white'
                      }`}
                      data-testid={`service-btn-${s.id}`}
                    >
                      {s.emoji} {s.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Nome */}
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <input
                  type="text"
                  placeholder="Seu nome *"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  required
                  className="w-full bg-slate-800/50 border border-slate-700 hover:border-slate-600 focus:border-cyan-500 text-white placeholder-slate-500 rounded-xl pl-10 pr-4 py-2.5 text-sm outline-none transition-colors"
                  data-testid="agendar-name"
                />
              </div>

              {/* Telefone */}
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <input
                  type="tel"
                  placeholder="WhatsApp / Telefone *"
                  value={phone}
                  onChange={e => setPhone(e.target.value)}
                  required
                  className="w-full bg-slate-800/50 border border-slate-700 hover:border-slate-600 focus:border-cyan-500 text-white placeholder-slate-500 rounded-xl pl-10 pr-4 py-2.5 text-sm outline-none transition-colors"
                  data-testid="agendar-phone"
                />
              </div>

              {/* Data preferida */}
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <input
                  type="date"
                  value={date}
                  onChange={e => setDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full bg-slate-800/50 border border-slate-700 hover:border-slate-600 focus:border-cyan-500 text-white placeholder-slate-500 rounded-xl pl-10 pr-4 py-2.5 text-sm outline-none transition-colors [color-scheme:dark]"
                  data-testid="agendar-date"
                />
              </div>

              {/* Observações */}
              <textarea
                placeholder="Descreva o problema brevemente (opcional)"
                value={message}
                onChange={e => setMessage(e.target.value)}
                rows={2}
                className="w-full bg-slate-800/50 border border-slate-700 hover:border-slate-600 focus:border-cyan-500 text-white placeholder-slate-500 rounded-xl px-4 py-2.5 text-sm outline-none transition-colors resize-none"
                data-testid="agendar-message"
              />

              {status === 'error' && (
                <p className="text-red-400 text-xs text-center">Erro ao enviar. Tente pelo WhatsApp abaixo.</p>
              )}

              {/* Botões */}
              <div className="flex gap-3 pt-1">
                <button
                  type="submit"
                  disabled={!service || !name || !phone || status === 'loading'}
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-gradient-to-r from-violet-600 to-cyan-600 hover:from-violet-700 hover:to-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-xl text-sm transition-all"
                  data-testid="agendar-submit"
                >
                  {status === 'loading' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Calendar className="w-4 h-4" />}
                  {status === 'loading' ? 'Enviando...' : 'Confirmar Agendamento'}
                </button>
              </div>

              {/* WhatsApp fallback */}
              <div className="border-t border-slate-800 pt-3 flex items-center justify-center">
                <a
                  href={whatsappLink()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-emerald-400 hover:text-emerald-300 text-xs font-medium transition-colors"
                  data-testid="agendar-whatsapp"
                >
                  <MessageCircle className="w-4 h-4" />
                  Prefere pelo WhatsApp? Clique aqui
                </a>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default AgendarDiagnosticoModal;
